import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { WifiOff, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { Eyebrow, PageHeader, StraightTalk } from "../components/bits";
import ActivationSimulator from "../components/poc/ActivationSimulator";
import GuestPassDemo from "../components/poc/GuestPassDemo";
import { SoundVideo } from "../components/SoundVideo";
import { VideoLightbox } from "../components/VideoLightbox";
import { IMG } from "../data/content";

const TEAL = "#3E6E8C";
const GREEN = "#2FD576";
const NAVY = "#0E1B33";

// dot = feature anchor on image (%), pill = label position (%)
const LABELS = [
  { key: "cloud", text: "Cloud Control Plane", color: TEAL, dashed: true, dot: [41, 14], pill: [7, 10], side: "left" },
  { key: "token", text: "Entitlement Token", color: TEAL, dashed: true, dot: [43, 30], pill: [7, 40], side: "left" },
  { key: "transmitter", text: "Transmitter", color: NAVY, dashed: false, dot: [49, 47], pill: [8, 72], side: "left" },
  { key: "coil", text: "Revo Tag", color: GREEN, dashed: false, dot: [66, 37], pill: [92, 50], side: "right" },
  { key: "cable", text: "Power cable", color: NAVY, dashed: false, dot: [27, 70], pill: [11, 90], side: "left" },
];

export default function HowItWorks() {
  const [lightbox, setLightbox] = useState(null);
  return (
    <Layout>
      <PageHeader
        eyebrow="How it works"
        title="Two planes. One experience."
        subtitle="Energy moves locally — from your wall outlets, through ChargeRevo transmitters, into your devices. Intelligence lives in the cloud — deciding which devices may charge, metering every watt-hour, protecting battery health, and handling billing. The cloud never carries the power. It carries the permission."
      />

      {/* TWO-PLANE DIAGRAM */}
      <section className="bg-white px-5 pb-8 pt-4 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <figure className="relative mx-auto overflow-hidden rounded-[2rem] border border-navy/10 bg-revo-surface" data-testid="two-plane-cutaway">
              <motion.img
                src={IMG.twoPlaneCutaway}
                alt="Cutaway diagram: wall outlet to a Revo transmitter mounted flush under a solid desk, green energy field rising through the wood into the laptop's Revo Tag receiver (a thin cable routes to the laptop's USB-C port), with a separate dotted entitlement token from the cloud control plane"
                className="block w-full"
                initial={{ scale: 1.04, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              />

              {/* leader lines (scale with image) */}
              <svg className="pointer-events-none absolute inset-0 hidden h-full w-full md:block" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                {LABELS.map((l) => (
                  <line key={l.key} x1={l.pill[0]} y1={l.pill[1]} x2={l.dot[0]} y2={l.dot[1]} stroke={l.color} strokeWidth="1" strokeDasharray={l.dashed ? "2 2" : "0"} vectorEffect="non-scaling-stroke" opacity="0.7" />
                ))}
              </svg>

              {/* anchor dots */}
              {LABELS.map((l) => (
                <span key={l.key} className="absolute hidden h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full md:block" style={{ left: `${l.dot[0]}%`, top: `${l.dot[1]}%`, background: l.color, boxShadow: `0 0 8px ${l.color}` }} aria-hidden />
              ))}

              {/* label pills */}
              {LABELS.map((l) => (
                <div
                  key={l.key}
                  className={`absolute hidden -translate-y-1/2 md:block ${l.side === "right" ? "-translate-x-full" : ""}`}
                  style={{ left: `${l.pill[0]}%`, top: `${l.pill[1]}%` }}
                >
                  <span className="whitespace-nowrap rounded-full border border-navy/10 bg-white/90 px-3 py-1.5 font-display text-xs font-semibold text-navy shadow-sm backdrop-blur">
                    {l.text}
                  </span>
                </div>
              ))}
            </figure>
          </Reveal>

          {/* mobile label legend */}
          <div className="mt-5 grid grid-cols-2 gap-2 md:hidden" data-testid="mobile-diagram-legend">
            {LABELS.map((l) => (
              <div key={l.key} className="flex items-center gap-2 rounded-xl border border-navy/10 bg-revo-surface px-3 py-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: l.color }} />
                <span className="text-xs font-semibold text-navy">{l.text}</span>
              </div>
            ))}
          </div>

          {/* control precedes power */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-center">
            <motion.div className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white px-5 py-3" data-testid="legend-control" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
              <span className="flex h-7 w-10 items-center" aria-hidden>
                <span className="h-0 w-full border-t-2 border-dashed" style={{ borderColor: "#3E6E8C" }} />
              </span>
              <div>
                <p className="font-display text-sm font-bold text-navy">1 · Control plane</p>
                <p className="text-xs text-navy/55">The cloud issues an entitlement token — data, not power.</p>
              </div>
            </motion.div>
            <motion.div className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white px-5 py-3" data-testid="legend-power" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.55 }}>
              <span className="flex h-7 w-10 items-center" aria-hidden>
                <span className="h-1 w-full rounded-full bg-revo-green" style={{ boxShadow: "0 0 12px rgba(47,213,118,0.7)" }} />
              </span>
              <div>
                <p className="font-display text-sm font-bold text-navy">2 · Power plane</p>
                <p className="text-xs text-navy/55">Only then do watts flow locally, through the surface.</p>
              </div>
            </motion.div>
          </div>
          <p className="mt-4 text-center text-sm text-navy/50">Control precedes power. The cloud carries the permission — never the energy.</p>
        </div>
      </section>

      {/* NO WIFI NEEDED */}
      <section className="bg-white px-5 pb-24 md:px-8">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <div className="flex flex-col items-start gap-6 rounded-3xl border border-revo-green/30 bg-navy p-8 md:flex-row md:items-center md:p-10" data-testid="no-wifi-callout">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-revo-green/15 text-revo-green">
                <WifiOff className="h-8 w-8" />
              </span>
              <div>
                <h2 className="font-display text-2xl font-black tracking-tight text-white sm:text-3xl">No WiFi. No internet. Still charging.</h2>
                <p className="mt-3 text-lg leading-relaxed text-revo-ice">
                  Charging is entirely local — power flows from your wall outlets through ChargeRevo transmitters into your devices with no internet involved. The cloud only handles enrollment, metering, and billing, and every transmitter caches its permissions. So if your WiFi drops or the internet goes out, your devices keep charging exactly as before. You only need a connection now and then to add a new device and sync usage.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE SURFACE */}
      <section className="bg-white px-5 pb-24 md:px-8">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-navy/10 bg-revo-surface">
              <img src={IMG.deskCutaway} alt="Cross-section of a desk with ChargeRevo transmitter" className="w-full" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow dark>The surface</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">Your desk is the charger.</h2>
            <p className="mt-5 text-lg leading-relaxed text-navy/70">
              A slim ChargeRevo transmitter mounts under any non-metal desktop and plugs into the outlet you already have. Its coil array covers a wide area of the surface, so there's no pad, no alignment, no ritual. Set a laptop anywhere on the desk and it fast-charges through the wood at 65 watts or more — faster than most laptops drain, so the battery climbs while you work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOW IT'S CONNECTED — INSTALL VIEWS */}
      <section className="bg-white px-5 pb-24 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal className="mb-10">
            <Eyebrow dark>How it's connected</Eyebrow>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">One tidy cord to the wall — then never again.</h2>
            <p className="mt-4 max-w-2xl text-navy/60">The promise was never that cords don't exist — it's that there are none in your life. A transmitter plugs into the outlet you already have, clips out of sight, and disappears. That's the last cable you'll touch.</p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="overflow-hidden rounded-3xl border border-navy/10 bg-revo-surface" data-testid="install-home">
                <div className="aspect-[4/3] overflow-hidden"><img src={IMG.homeInstall} alt="ChargeRevo Surface mounted under a home-office desk with one tidy cord" className="h-full w-full object-cover" /></div>
                <p className="p-5 text-sm text-navy/60"><span className="font-semibold text-navy">Under your desk.</span> One Surface, one clipped cord to a standard outlet. Ten-minute install.</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-3xl border border-navy/10 bg-revo-surface" data-testid="install-conference">
                <div className="aspect-[4/3] overflow-hidden"><img src={IMG.confInstall} alt="ChargeRevo panels mounted under a conference table with one cable-managed cord" className="h-full w-full object-cover" /></div>
                <p className="p-5 text-sm text-navy/60"><span className="font-semibold text-navy">Under the boardroom table.</span> Multi-zone panels, one cable-managed run to a floor box. The table stays bare.</p>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <figure className="mt-6 overflow-hidden rounded-3xl border border-navy/10 bg-navy" data-testid="install-power-video">
              <div className="aspect-video overflow-hidden">
                <SoundVideo src="/videos/power.mp4" poster="/videos/power-poster.jpg" testid="install-power-clip" className="h-full w-full object-cover" onExpand={() => setLightbox({ src: "/videos/power.mp4", poster: "/videos/power-poster.jpg" })} />
              </div>
              <figcaption className="p-5 text-sm text-revo-ice/70"><span className="font-semibold text-white">One cord to the wall.</span> A single transmitter draws power from an outlet you already have — then every surface it feeds charges wirelessly. Nothing else on your desk ever plugs in.</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* THE RECEIVER ON YOUR DEVICE */}
      <section className="bg-revo-surface px-5 py-24 md:px-8" data-testid="hiw-receiver-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-navy/10 bg-white">
              <div className="aspect-square overflow-hidden">
                <img src={IMG.revoTagInstalled} alt="Revo Tag receiver installed on a laptop underside, nearly invisible" className="h-full w-full object-cover" data-testid="hiw-tag-image" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow dark>The only thing on your device</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">A receiver so slim, you forget it&apos;s there.</h2>
            <p className="mt-5 text-lg leading-relaxed text-navy/70">
              Transmitters live out of sight under your surfaces. The only thing that touches your device is a slim receiver — like the adhesive <span className="font-semibold text-navy">Revo Tag</span> bonded flush to your laptop&apos;s underside here. A hair-thin cable seats into the USB-C port, and from then on the laptop charges the instant it meets any Revo Surface. No pads, no plugging in, nothing to remember.
            </p>
            <Link to="/products#tag" className="mt-6 inline-flex items-center gap-2 font-semibold text-navy transition-colors hover:text-revo-green" data-testid="hiw-tag-link">
              See the receiver family <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SEE IT INSTALLED — VIDEO ILLUSTRATIONS */}
      <section className="bg-white px-5 pb-24 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal className="mb-10">
            <Eyebrow dark>Once it&apos;s installed</Eyebrow>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">See it the moment it&apos;s live.</h2>
            <p className="mt-4 max-w-2xl text-navy/60">The tangle of cables clears, the surface comes alive, and every device charges the instant it&apos;s set down — no pads, no plugs, no ritual.</p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            <Reveal>
              <figure className="overflow-hidden rounded-3xl border border-navy/10 bg-revo-surface" data-testid="installed-video-1">
                <div className="aspect-video overflow-hidden bg-navy">
                  <SoundVideo src="/videos/fill1.mp4" poster="/videos/fill1-poster.jpg" testid="installed-clip-1" className="h-full w-full object-cover" onExpand={() => setLightbox({ src: "/videos/fill1.mp4", poster: "/videos/fill1-poster.jpg" })} />
                </div>
                <figcaption className="p-5 text-sm text-navy/60"><span className="font-semibold text-navy">Set down, and charge.</span> Cables disappear from the table — phones and devices power up the instant they touch the ChargeRevo surface.</figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.1}>
              <figure className="overflow-hidden rounded-3xl border border-navy/10 bg-revo-surface" data-testid="installed-video-2">
                <div className="aspect-video overflow-hidden bg-navy">
                  <SoundVideo src="/videos/fill2.mp4" poster="/videos/fill2-poster.jpg" testid="installed-clip-2" className="h-full w-full object-cover" onExpand={() => setLightbox({ src: "/videos/fill2.mp4", poster: "/videos/fill2-poster.jpg" })} />
                </div>
                <figcaption className="p-5 text-sm text-navy/60"><span className="font-semibold text-navy">The whole table, powered.</span> Laptops fast-charge straight through the boardroom table while the meeting keeps moving.</figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE ROOM */}
      <section className="bg-revo-surface px-5 py-24 md:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <Eyebrow dark>The room</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">The room keeps you topped up.</h2>
            <p className="mt-5 text-lg leading-relaxed text-navy/70">
              A ceiling-mounted ChargeRevo unit washes the space with low, certified levels of RF and infrared power. It can't fast-charge a laptop — nothing at a distance safely can — but it doesn't need to. A few ambient watts slow your drain between surfaces, refill devices while they sleep, and keep small devices like phones, remotes, and sensors alive indefinitely.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ACTIVATION POC */}
      <section className="bg-navy px-5 py-24 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal className="mb-10 text-center">
            <Eyebrow>The five-minute activation</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">From box to charging in five minutes.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-revo-ice">
              Every ChargeRevo device carries a unique serial. Scan it in the app; the cloud binds it to your account and authorizes your transmitters — and every watt-hour it ever receives is metered to it, wherever in the world it charges.
            </p>
          </Reveal>
          <Reveal delay={0.1}><ActivationSimulator /></Reveal>
        </div>
      </section>

      {/* GUEST PASS / ENTITLEMENT */}
      <section className="bg-navy px-5 pb-24 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal className="mb-10 text-center">
            <Eyebrow>Permission, not power</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">You decide who charges — down to the minute.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-revo-ice">
              The same cloud that meters your energy governs who's allowed to use it. Grant a guest a time-boxed pass and watch the entitlement issue, count down, and revoke — while everyone else on your account stays untouched.
            </p>
          </Reveal>
          <Reveal delay={0.1}><GuestPassDemo /></Reveal>
        </div>
      </section>

      {/* HONEST PHYSICS */}
      <StraightTalk title="What ChargeRevo will never claim.">
        <p>
          Radio energy spreads and weakens with distance — double the distance, quarter the power. Beaming laptop-class wattage across a room would fail every human-safety limit, and no honest company will sell you that.
        </p>
        <p>
          ChargeRevo's answer is coverage, not beams: full power where you set things down, gentle top-up everywhere else, and a battery that refills so fast you never notice the difference.
        </p>
      </StraightTalk>

      <VideoLightbox open={!!lightbox} src={lightbox?.src} poster={lightbox?.poster} onClose={() => setLightbox(null)} />
    </Layout>
  );
}
